import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { resetPasswordInfo, resetPassword, formatApiError } from "@/auth";
import { AuthShell, Field, ErrorBox, PrimaryButton } from "@/components/AuthShell";

export const ResetPassword = ({ onAuthed }) => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!token) { setInvalid("This reset link is missing its token."); return; }
    resetPasswordInfo(token).then(setInfo).catch((e) => setInvalid(formatApiError(e, "This reset link is invalid or has expired.")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true); setError("");
    try {
      const u = await resetPassword(token, password);
      onAuthed && onAuthed(u);
      nav("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Could not reset the password."));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell testId="reset-screen" title="Choose a new password" subtitle={info ? `Resetting the password for ${info.email}.` : undefined}>
      {invalid ? (
        <>
          <ErrorBox error={invalid} testId="reset-invalid" />
          <p className="mt-5 text-center text-xs text-slate-500">
            <Link data-testid="request-new-link" to="/forgot-password" className="text-orange-400 hover:underline">Request a new reset link</Link>
          </p>
        </>
      ) : !info ? (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Checking link…</div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <Field label="New password" testId="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters, a letter and a number" required autoFocus />
          <Field label="Confirm new password" testId="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" required />
          <ErrorBox error={error} testId="reset-error" />
          <PrimaryButton testId="reset-submit" type="submit" disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Set new password &amp; sign in
          </PrimaryButton>
        </form>
      )}
    </AuthShell>
  );
};

export default ResetPassword;
